import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { TextField } from "@mui/material";
import { managementFeesApi, FeeInvoiceFile, FeeFileError, FeeFileType } from "@/services/api";
import { Button, FileDropzone, FilterMultiSelect, FilterOption, FilterSelect, Modal, Note, confirmColors } from "@/components/ui";
import { ColumnDef } from "@/components/utils/DataTable";
import { StaticTable } from "@/components/client_performance/ReportSections";
import { useSchemeOptions } from "@/hooks/useSchemeOptions";
import { describeRequestError, joinFieldErrors } from "@/utils/apiErrors";
import { formatDateTime } from "@/utils/formatters";
import { formatQuarterLabel, fyQuarterEndDate, fyQuarterOfDate, getCurrentFYQuarter, getRecentFYQuarters } from "@/utils/fy";
import { monthEndDate, previousMonthValue } from "@/components/soa/monthEnd";

const FILE_TYPE_OPTIONS: FilterOption[] = [
    { value: "BILL_REGISTER", label: "Bill Register (quarterly)" },
    { value: "INVOICE_DETAIL", label: "Invoice Detail (monthly)" },
];
const QUARTER_OPTIONS: FilterOption[] = getRecentFYQuarters().map(q => ({ value: q, label: formatQuarterLabel(q) }));

const ALL = "ALL";
const TYPE_FILTER_OPTIONS: FilterOption[] = [{ value: ALL, label: "All" }, ...FILE_TYPE_OPTIONS];
const periodKey = (f: Pick<FeeInvoiceFile, "file_type" | "period">) => `${f.file_type}|${f.period}`;

const formatPeriod = (fileType: FeeFileType, periodEnd: string): string =>
    fileType === "BILL_REGISTER" ? formatQuarterLabel(fyQuarterOfDate(periodEnd)) : format(parseISO(periodEnd), "MMM yyyy");

const ERROR_COLUMNS: ColumnDef<FeeFileError>[] = [
    { key: "row", header: "Row", width: "70px", render: e => e.row },
    { key: "column", header: "Column", width: "170px", render: e => e.column },
    { key: "value", header: "Value", width: "200px", render: e => e.value },
    { key: "reason", header: "Reason", render: e => e.reason },
];

const WRAP: React.CSSProperties = { whiteSpace: "normal", overflowWrap: "anywhere" };
const wrap = (text: React.ReactNode) => <span style={WRAP}>{text}</span>;

const HISTORY_COLUMNS: ColumnDef<FeeInvoiceFile>[] = [
    { key: "created_ts", header: "Uploaded At", width: "130px", render: f => wrap(formatDateTime(f.created_ts)) },
    { key: "period", header: "Period", width: "140px", render: f => wrap(formatPeriod(f.file_type, f.period)) },
    { key: "scheme_codes", header: "Schemes", width: "130px", render: f => wrap(f.scheme_codes.join(", ")) },
    { key: "file_name", header: "File", render: f => wrap(f.file_name) },
    { key: "uploaded_by", header: "Uploaded By", width: "120px", render: f => wrap(f.uploaded_by ?? "—") },
];

interface UploadResult {
    message: string;
    rows: FeeFileError[];
}

const describeFileError = (e: FeeFileError) => {
    const where = e.row ? `Row ${e.row}, ${e.column}` : "";
    const detail = [where, e.value].filter(Boolean).join(": ");
    return detail ? `${e.reason} (${detail})` : e.reason;
};

const readUploadError = (err: unknown): UploadResult => {
    const data: unknown = axios.isAxiosError(err) ? err.response?.data : undefined;
    const fileErrors: FeeFileError[] =
        data && typeof data === "object" && "file_errors" in data && Array.isArray(data.file_errors) ? data.file_errors : [];
    if (fileErrors.length === 1) {
        return { rows: [], message: `File not uploaded. ${describeFileError(fileErrors[0])}.` };
    }
    if (fileErrors.length) {
        return { rows: fileErrors, message: `File not uploaded. ${fileErrors.length} issues were found; fix them (listed below) and upload again.` };
    }
    const detail = data && typeof data === "object" ? joinFieldErrors(data) : "";
    return { rows: [], message: detail || describeRequestError(err, "upload the file") };
};

interface FeeInvoiceFilesModalProps {
    onClose: () => void;
}

const FeeInvoiceFilesModal: React.FC<FeeInvoiceFilesModalProps> = ({ onClose }) => {
    const { schemeOptions, loading: loadingSchemes, error: schemesError } = useSchemeOptions();
    const [fileType, setFileType] = useState<FeeFileType>("BILL_REGISTER");
    const [schemes, setSchemes] = useState<FilterOption[]>([]);
    const [quarter, setQuarter] = useState(getCurrentFYQuarter());
    const [month, setMonth] = useState(previousMonthValue());
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<UploadResult | null>(null);
    const [history, setHistory] = useState<FeeInvoiceFile[]>([]);
    const [typeFilter, setTypeFilter] = useState(ALL);
    const [periodFilter, setPeriodFilter] = useState<string | null>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const historyRequest = useRef(0);

    const period = fileType === "BILL_REGISTER" ? fyQuarterEndDate(quarter) : monthEndDate(month);
    const message = result?.message ?? schemesError;

    const loadHistory = async () => {
        const request = ++historyRequest.current;
        setLoadingHistory(true);
        try {
            const response = await managementFeesApi.listInvoiceFiles({ page_size: 1000 });
            if (request === historyRequest.current) setHistory(response.results);
        } catch {
            if (request === historyRequest.current) setResult({ message: "Failed to load uploaded files.", rows: [] });
        } finally {
            if (request === historyRequest.current) setLoadingHistory(false);
        }
    };

    useEffect(() => {
        loadHistory();
    }, []);

    const handleClose = () => {
        if (!uploading) onClose();
    };

    const handleUpload = async () => {
        if (!file || !schemes.length || !period) return;
        setUploading(true);
        setResult(null);
        try {
            await managementFeesApi.uploadInvoiceFile(fileType, period, schemes.map(s => s.value), file);
            toast.success(`${file.name} uploaded for ${formatPeriod(fileType, period)}`);
            setFile(null);
            setTypeFilter(ALL);
            setPeriodFilter(periodKey({ file_type: fileType, period }));
            loadHistory();
        } catch (err) {
            setResult(readUploadError(err));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (feeFile: FeeInvoiceFile) => {
        const confirmation = await Swal.fire({
            title: "Delete file?",
            text: `${feeFile.file_name} (${formatPeriod(feeFile.file_type, feeFile.period)}) will be removed.`,
            icon: "warning",
            showCancelButton: true,
            ...confirmColors("danger"),
            confirmButtonText: "Delete",
        });
        if (!confirmation.isConfirmed) return;
        try {
            await managementFeesApi.deleteInvoiceFile(feeFile.id);
            toast.success(`${feeFile.file_name} deleted`);
            loadHistory();
        } catch {
            toast.error(`Could not delete ${feeFile.file_name}`);
        }
    };

    const periodOptions = useMemo(() => {
        const byKey = new Map<string, FilterOption>();
        [...history]
            .filter(f => typeFilter === ALL || f.file_type === typeFilter)
            .sort((a, b) => b.period.localeCompare(a.period))
            .forEach(f => byKey.set(periodKey(f), { value: periodKey(f), label: formatPeriod(f.file_type, f.period) }));
        return [{ value: ALL, label: "All" }, ...byKey.values()];
    }, [history, typeFilter]);
    // Until the user picks one, show the latest period.
    const selectedPeriod = periodFilter ?? periodOptions[1]?.value ?? ALL;
    const visibleHistory = history.filter(
        f => (typeFilter === ALL || f.file_type === typeFilter) && (selectedPeriod === ALL || periodKey(f) === selectedPeriod),
    );

    const historyColumns: ColumnDef<FeeInvoiceFile>[] = [
        ...HISTORY_COLUMNS,
        {
            key: "actions",
            header: "",
            width: "80px",
            render: f => <Button size="sm" variant="danger" onClick={() => handleDelete(f)}>Delete</Button>,
        },
    ];

    return (
        <Modal
            open
            title="Bill / Invoice Files"
            onClose={handleClose}
            isSubmitting={uploading}
            maxWidth={900}
            footer={
                <>
                    <Button onClick={handleClose} disabled={uploading}>Close</Button>
                    <Button variant="primary" onClick={handleUpload} disabled={!file || !schemes.length || !period || uploading}>
                        {uploading ? "Uploading…" : "Upload"}
                    </Button>
                </>
            }
        >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr", gap: 12, marginBottom: 12 }}>
                <FilterSelect
                    label="File Type"
                    options={FILE_TYPE_OPTIONS}
                    value={FILE_TYPE_OPTIONS.find(o => o.value === fileType) ?? null}
                    onChange={opt => { setFileType((opt?.value as FeeFileType) ?? "BILL_REGISTER"); setResult(null); }}
                />
                <FilterMultiSelect
                    label="Schemes"
                    options={schemeOptions}
                    value={schemes}
                    onChange={setSchemes}
                    loading={loadingSchemes}
                />
                {fileType === "BILL_REGISTER" ? (
                    <FilterSelect
                        label="Quarter"
                        options={QUARTER_OPTIONS}
                        value={QUARTER_OPTIONS.find(o => o.value === quarter) ?? null}
                        onChange={opt => setQuarter(opt?.value ?? getCurrentFYQuarter())}
                    />
                ) : (
                    <TextField
                        label="Month"
                        type="month"
                        size="small"
                        value={month}
                        onChange={event => setMonth(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                )}
            </div>

            <FileDropzone
                accept={[".xlsx", ".csv"]}
                onFiles={files => { setFile(files[0] ?? null); setResult(null); }}
                file={file}
                onRemove={() => setFile(null)}
                busy={uploading}
                mainText="Click or drag & drop the Nuvama file"
                subText={fileType === "BILL_REGISTER" ? "Nuvama Bill Register — .xlsx or .csv" : "Nuvama Invoice Detail Summary — .xlsx or .csv"}
            />

            {message && <div style={{ marginTop: 12 }}><Note tone="error">{message}</Note></div>}

            {!!result?.rows.length && (
                <StaticTable title="Problems" data={result.rows} columns={ERROR_COLUMNS} rowKey={(_, index) => index} maxBodyHeight="240px" />
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "16px 0 4px" }}>
                <FilterSelect
                    label="Type"
                    options={TYPE_FILTER_OPTIONS}
                    value={TYPE_FILTER_OPTIONS.find(o => o.value === typeFilter) ?? null}
                    onChange={opt => { setTypeFilter(opt?.value ?? ALL); setPeriodFilter(null); }}
                />
                <FilterSelect
                    label="Period"
                    options={periodOptions}
                    value={periodOptions.find(o => o.value === selectedPeriod) ?? null}
                    onChange={opt => setPeriodFilter(opt?.value ?? ALL)}
                />
            </div>

            <StaticTable
                title="Uploaded Files"
                data={visibleHistory}
                columns={historyColumns}
                rowKey={f => f.id}
                loading={loadingHistory}
                maxBodyHeight="240px"
                emptyMessage="No files uploaded yet."
            />
        </Modal>
    );
};

export default FeeInvoiceFilesModal;
